"use client";

import { motion, useInView, useReducedMotion } from "framer-motion";
import { useRef } from "react";

const EASE = [0.16, 1, 0.3, 1];

/* gentle endless float, disabled when the user prefers reduced motion */
const float = (reduce, distance = 10, duration = 6, delay = 0) =>
  reduce
    ? {}
    : {
        animate: { y: [0, -distance, 0] },
        transition: { duration, delay, repeat: Infinity, ease: "easeInOut" },
      };

export default function QuoteDivider() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10%" });
  const reduce = useReducedMotion();

  return (
    <section ref={ref} className="relative py-10 md:py-14 overflow-hidden flex items-center justify-center">
      {/* BACKGROUND (unchanged) */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: "url('/bg.webp')",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundAttachment: "fixed",
        }}
      />

      {/* BLUE OVERLAY (unchanged) */}
      <div
        className="absolute inset-0 z-0"
        style={{
          background: "linear-gradient(135deg, rgba(0,102,153,0.88) 0%, rgba(0,34,51,0.95) 100%)",
        }}
      />

      {/* CONTENT: nothing boxed, everything floats on the background */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 sm:px-10 flex flex-col md:flex-row items-center gap-8 md:gap-14">
        {/* ── floating photo ── */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: EASE }}
          className="relative flex-shrink-0"
        >
          <motion.div {...float(reduce, 12, 6)} className="relative w-36 sm:w-44 md:w-52 lg:w-60">
            {/* offset outline frame */}
            <span className="absolute inset-0 border-2 border-[#7dd3fc]/60 translate-x-3 translate-y-3 pointer-events-none" />

            {/* photo */}
            <div className="relative aspect-[3/4] overflow-hidden shadow-[0_25px_60px_rgba(0,0,0,0.45)]">
              <img
                src="/henry.jpeg"
                alt="Henry Petroski"
                className="w-full h-full object-cover object-top grayscale hover:grayscale-0 transition-all duration-700"
                onError={(e) => { e.target.src = "/asset/gallery/g1 (1).jpg"; e.target.onerror = null; }}
              />
            </div>

            {/* corner brackets sticking out of the photo */}
            <span className="absolute -top-3 -left-3 w-9 h-9 border-t-2 border-l-2 border-[#7dd3fc] pointer-events-none" />
            <span className="absolute -bottom-3 -right-3 w-9 h-9 border-b-2 border-r-2 border-[#7dd3fc] pointer-events-none" />

            {/* floating name tag */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.7, duration: 0.7, ease: EASE }}
              className="absolute -left-4 sm:-left-8 bottom-5 bg-[#00334d] border border-[#7dd3fc]/40 px-3 py-2 shadow-[0_15px_40px_rgba(0,0,0,0.4)]"
            >
              <span className="inline-block bg-[#7dd3fc] text-[#00334d] text-[11px] sm:text-xs font-bold px-2 py-0.5 mb-2">
                American Engineer
              </span>
              <p className="text-sm font-light text-white/90 leading-none">Henry</p>
              <p className="text-lg sm:text-xl font-black text-white tracking-tight leading-tight">Petroski</p>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* ── floating quote ── */}
        <div className="relative flex-1 text-left">
          {/* huge outlined quote mark behind the text */}
          <motion.span
            {...float(reduce, 8, 7, 1)}
            className="absolute -top-10 -left-2 sm:-top-16 sm:-left-6 text-[120px] sm:text-[200px] leading-none font-black text-transparent select-none pointer-events-none"
            style={{ WebkitTextStroke: "2px rgba(125,211,252,0.35)" }}
            aria-hidden="true"
          >
            &ldquo;
          </motion.span>

          <motion.span
            initial={{ width: 0 }}
            animate={isInView ? { width: 56 } : {}}
            transition={{ delay: 0.3, duration: 0.8, ease: EASE }}
            className="relative block h-[3px] bg-[#7dd3fc] mb-4"
          />

          <motion.blockquote
            initial={{ opacity: 0, x: 24 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.8, ease: EASE }}
            className="relative"
          >
            <p className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight leading-[1.1] [text-shadow:0_2px_20px_rgba(0,0,0,0.35)]">
              As engineers, we were going to be in a position to change the world
              <span className="font-light text-white/80"> &ndash; not just study it.</span>
            </p>
          </motion.blockquote>

          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="relative mt-5 flex items-center gap-3"
          >
            <span className="h-[3px] w-10 bg-[#7dd3fc]" />
            <p className="text-sm sm:text-base font-semibold text-[#7dd3fc]">Henry Petroski</p>
          </motion.div>

          {/* floating decorative corner mark */}
          <motion.span
            {...float(reduce, 6, 5, 0.5)}
            className="hidden md:block absolute -bottom-6 right-0 w-10 h-10 border-b-2 border-r-2 border-[#7dd3fc]/70 pointer-events-none"
          />
        </div>
      </div>
    </section>
  );
}